import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import type {
  ChangeLeadQualificationStatusRequest,
  CreateLeadQualificationRequest,
  CreateLeadQualificationRulesRequest,
  CreateLeadQualificationRulesResponse,
  DeleteLeadQualificationRuleRequest,
  GetLeadQualificationRulesParams,
  GetLeadQualificationRulesResponse,
  GetLeadQualificationsResponse,
  ImportLeadQualificationRulesRequest,
  ImportLeadQualificationRulesResponse,
  PartnerLeadQualification,
  UpdateLeadQualificationRequest,
} from './models';

export const partnerLeadQualifications = `partner_lead_qualifications`;
const rulesTag = `${partnerLeadQualifications}_rules`;

const jsonHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

export const partnerLeadQualificationsApi = createApi({
  reducerPath: partnerLeadQualifications,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partnerLeadQualifications, rulesTag],
  endpoints: (build) => ({
    getLeadQualifications: build.query<GetLeadQualificationsResponse, void>({
      query: () => ({
        url: '/partner/lead-qualifications',
        method: 'GET',
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: partnerLeadQualifications, id: 'LIST' }],
    }),
    createLeadQualification: build.mutation<
      { data: PartnerLeadQualification },
      CreateLeadQualificationRequest
    >({
      query: (body) => ({
        url: '/partner/lead-qualifications',
        method: 'POST',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [{ type: partnerLeadQualifications, id: 'LIST' }],
    }),
    updateLeadQualification: build.mutation<
      { data: PartnerLeadQualification },
      UpdateLeadQualificationRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/partner/lead-qualifications/${id}`,
        method: 'PATCH',
        body,
        headers: jsonHeaders,
      }),
      invalidatesTags: [{ type: partnerLeadQualifications, id: 'LIST' }],
    }),
    changeLeadQualificationStatus: build.mutation<
      { data: PartnerLeadQualification },
      ChangeLeadQualificationStatusRequest
    >({
      query: ({ id, status, transition }) => ({
        url: `/partner/lead-qualifications/${id}/status/${status}`,
        method: 'PATCH',
        body: { transition },
        headers: jsonHeaders,
      }),
      invalidatesTags: [{ type: partnerLeadQualifications, id: 'LIST' }],
    }),
    getLeadQualificationRules: build.query<
      GetLeadQualificationRulesResponse,
      GetLeadQualificationRulesParams
    >({
      query: ({ qualificationId, page, per_page, search }) => ({
        url: `/partner/lead-qualifications/${qualificationId}/rules`,
        method: 'GET',
        params: {
          ...(page ? { page } : {}),
          ...(per_page ? { per_page } : {}),
          ...(search ? { 'filters[search]': search } : {}),
        },
        headers: { Accept: 'application/json' },
      }),
      providesTags: (_result, _error, { qualificationId }) => [
        { type: rulesTag, id: qualificationId },
      ],
    }),
    createLeadQualificationRules: build.mutation<
      CreateLeadQualificationRulesResponse,
      CreateLeadQualificationRulesRequest
    >({
      query: ({ qualificationId, rules }) => ({
        url: `/partner/lead-qualifications/${qualificationId}/rules`,
        method: 'POST',
        body: { rules },
        headers: jsonHeaders,
      }),
      invalidatesTags: (_r, _e, { qualificationId }) => [
        { type: rulesTag, id: qualificationId },
        { type: partnerLeadQualifications, id: 'LIST' },
      ],
    }),
    importLeadQualificationRules: build.mutation<
      ImportLeadQualificationRulesResponse,
      ImportLeadQualificationRulesRequest
    >({
      query: ({ qualificationId, file }) => {
        const body = new FormData();
        body.append('file', file);

        return {
          url: `/partner/lead-qualifications/${qualificationId}/rules/import`,
          method: 'POST',
          body,
          headers: { Accept: 'application/json' },
        };
      },
      invalidatesTags: (_r, _e, { qualificationId }) => [
        { type: rulesTag, id: qualificationId },
        { type: partnerLeadQualifications, id: 'LIST' },
      ],
    }),
    deleteLeadQualificationRule: build.mutation<void, DeleteLeadQualificationRuleRequest>({
      query: ({ qualificationId, ruleId }) => ({
        url: `/partner/lead-qualifications/${qualificationId}/rules/${ruleId}`,
        method: 'DELETE',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: (_r, _e, { qualificationId }) => [
        { type: rulesTag, id: qualificationId },
        { type: partnerLeadQualifications, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetLeadQualificationsQuery,
  useCreateLeadQualificationMutation,
  useUpdateLeadQualificationMutation,
  useChangeLeadQualificationStatusMutation,
  useGetLeadQualificationRulesQuery,
  useCreateLeadQualificationRulesMutation,
  useImportLeadQualificationRulesMutation,
  useDeleteLeadQualificationRuleMutation,
} = partnerLeadQualificationsApi;
